import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import AiSparkIcon from '../common/AiSparkIcon';
import { resolveDocTitle, subjectDocTitle, subjectNamesFromStandards } from '../../utils/evaluationPlanDocTitle';
import type {
  EvaluationLevelDisplayLabels,
  EvaluationLevelStepCount,
  EvaluationPlanAttachment,
  EvaluationPlanAttachmentSlot,
  EvaluationPlanDraft,
  EvaluationStandardDraft,
} from '../../types/evaluationPlan';
import { getActiveLevelKeys, getActiveLevelLabels } from '../../types/evaluationPlan';
import {
  findLearningContentsForStandards,
  learningSubCategoryLabel,
  normalizeAchievementCode,
  type LearningContentMatchLesson,
  type LearningContentMatchUnit,
} from '../../utils/evaluationLearningContentMatch';
import {
  AI_EVAL_ASSESSMENT_TYPES,
  AI_EVAL_DIFFICULTIES,
  fetchAiEvalStatus,
  requestAiEval,
  type AiEvalContext,
  type AiEvalFieldsResult,
  type AiEvalOptions,
  type AiEvalRate,
  type AiEvalRubricResult,
  type AiEvalSheetResult,
} from '../../utils/aiEvaluationCompose';
import {
  buildRubricWebDocumentFromAi,
  buildRubricWebDocumentFromTemplate,
  buildSheetCanvasDocumentFromAi,
  collectRubricTemplateFields,
} from '../../utils/aiEvaluationBuilders';
import type { EvaluationPlanWebDocumentTemplate } from '../../utils/evaluationPlanWebDocumentTemplateStorage';
import { listEvaluationWebDocumentTemplates } from '../../services/evaluationWebDocumentTemplateService';
import { parseEvaluationPlanWebDocumentData } from '../../types/evaluationPlanWebDocument';
import { sanitizeSheetCodeHtml } from '../../utils/sheetCodeHtmlSanitize';

/** AI 기준안 양식 선택 기억(이 브라우저) */
const RUBRIC_TEMPLATE_CHOICE_KEY = 'cp.aiEval.rubricTemplateId.v1';
const DEFAULT_RUBRIC_TEMPLATE = '__default__';

/** 평가요소 1개로: 여러 줄·글머리표면 첫 요소만(단순 이어 붙이지 않음) */
function toSingleEvaluationElement(raw: unknown): string {
  const text = Array.isArray(raw) ? raw.map(String).join('\n') : String(raw ?? '');
  const lines = text
    .split(/\n|(?=▪)/)
    .map((l) => l.replace(/^[\s▪·•\-]+/, '').replace(/[.。]\s*$/, '').trim())
    .filter(Boolean);
  return lines[0] ?? '';
}

type FieldKey = 'unit_text' | 'evaluation_element_text' | 'evaluation_method_text';
const FIELD_LABELS: Record<FieldKey, string> = {
  unit_text: '단원명',
  evaluation_element_text: '평가요소',
  evaluation_method_text: '수업·평가 방법',
};
const FIELD_KEYS: FieldKey[] = ['unit_text', 'evaluation_element_text', 'evaluation_method_text'];
const STEP_TITLES = ['학습내용 선택', '평가계획 항목', '기준안', '평가지'];

type Props = {
  open: boolean;
  onClose: () => void;
  workspaceId: string | null;
  categoryName: string;
  draft: EvaluationPlanDraft;
  allStandards: EvaluationStandardDraft[];
  levelStepCount: EvaluationLevelStepCount;
  levelDisplayLabels: Partial<EvaluationLevelDisplayLabels> | null;
  hasCriterion: boolean;
  hasSheet: boolean;
  existingCriterionAttachment: EvaluationPlanAttachment | null;
  existingSheetAttachment?: EvaluationPlanAttachment | null;
  canUndo: boolean;
  onApplyFields: (patch: Partial<EvaluationPlanDraft>) => void;
  onApplyAttachment: (slot: EvaluationPlanAttachmentSlot, attachment: EvaluationPlanAttachment) => void;
  onUndo: () => void;
  /** 사용자 화면: 현재 학급 학년(학습내용 검색 범위) */
  classScope?: { gradeId: string; gradeNumbers: number[] } | null;
};

const AiEvaluationComposeModal: React.FC<Props> = ({
  open,
  onClose,
  workspaceId,
  categoryName,
  draft,
  allStandards,
  levelStepCount,
  levelDisplayLabels,
  hasCriterion,
  hasSheet,
  existingCriterionAttachment,
  existingSheetAttachment = null,
  canUndo,
  onApplyFields,
  onApplyAttachment,
  onUndo,
  classScope = null,
}) => {
  const [step, setStep] = useState(0);
  const [units, setUnits] = useState<LearningContentMatchUnit[] | null>(null);
  const [unitsLoading, setUnitsLoading] = useState(false);
  const [unitsError, setUnitsError] = useState('');
  const [searchInfo, setSearchInfo] = useState<{ source: 'workspace' | 'admin'; gradeNumbers: number[] } | null>(null);
  /** 체크한 학습내용(여러 개, 그룹 넘어 가능) */
  const [selectedKeys, setSelectedKeys] = useState<string[]>([]);
  /** ① 적용으로 확정된 선택(②~④가 사용). null = 아직 적용 안 함 */
  const [appliedKeys, setAppliedKeys] = useState<string[] | null>(null);
  const appliedKeysRef = useRef<string[] | null>(null);
  appliedKeysRef.current = appliedKeys;
  const [options, setOptions] = useState<AiEvalOptions>({ questionCount: 2, difficulty: '보통', assessmentType: '서술형' });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [rate, setRate] = useState<AiEvalRate>(null);
  const [fieldsResult, setFieldsResult] = useState<AiEvalFieldsResult | null>(null);
  const [fieldOverwrite, setFieldOverwrite] = useState<Record<FieldKey, boolean>>({
    unit_text: true,
    evaluation_element_text: true,
    evaluation_method_text: true,
  });
  const [rubricResult, setRubricResult] = useState<AiEvalRubricResult | null>(null);
  const [sheetResult, setSheetResult] = useState<AiEvalSheetResult | null>(null);
  const [confirmReplace, setConfirmReplace] = useState(false);
  const [appliedSteps, setAppliedSteps] = useState<Record<number, boolean>>({});
  const [notice, setNotice] = useState('');
  /**
   * 단계별 '적용됨' 서명: 적용한 결과(result)의 서명 + 적용 후 대상(폼 값/첨부 문서)의 서명.
   * 현재 결과·대상 서명과 둘 다 같을 때만 '적용됨'.
   */
  const [appliedSig, setAppliedSig] = useState<{
    fields?: { result: string; patch: Partial<Record<FieldKey, string>> };
    rubric?: { result: string; target: string };
    sheet?: { result: string; target: string };
  }>({});
  const abortRef = useRef<AbortController | null>(null);
  /** 기준안 양식: '__default__'(기본 양식) 또는 저장한 내 양식 id */
  const [rubricTemplates, setRubricTemplates] = useState<EvaluationPlanWebDocumentTemplate[]>([]);
  const [rubricTemplateId, setRubricTemplateId] = useState(DEFAULT_RUBRIC_TEMPLATE);
  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    void listEvaluationWebDocumentTemplates('criterion')
      .then((list) => {
        if (!cancelled) pickInitialTemplate(list);
      })
      .catch(() => {
        if (!cancelled) pickInitialTemplate([]);
      });
    return () => {
      cancelled = true;
    };
  }, [open]);
  const pickInitialTemplate = (list: EvaluationPlanWebDocumentTemplate[]) => {
    setRubricTemplates(list);
    let remembered = '';
    try {
      remembered = localStorage.getItem(RUBRIC_TEMPLATE_CHOICE_KEY) ?? '';
    } catch {
      /* 무시 */
    }
    if (remembered === DEFAULT_RUBRIC_TEMPLATE || list.some((t) => t.id === remembered)) {
      setRubricTemplateId(remembered);
    } else {
      // 기억한 선택이 없으면 가장 최근에 저장한 내 양식, 없으면 기본 양식
      const recent = [...list].sort((a, b) => (b.savedAt || '').localeCompare(a.savedAt || ''))[0];
      setRubricTemplateId(recent ? recent.id : DEFAULT_RUBRIC_TEMPLATE);
    }
  };
  const selectedRubricTemplate = useMemo(
    () => rubricTemplates.find((t) => t.id === rubricTemplateId) ?? null,
    [rubricTemplates, rubricTemplateId]
  );
  const chooseRubricTemplate = (id: string) => {
    if (id === rubricTemplateId) return;
    setRubricTemplateId(id);
    // 양식이 바뀌면 이전 결과는 칸이 맞지 않으므로 지움(같은 조건이면 다시 만들 때 캐시 사용)
    setRubricResult(null);
    setConfirmReplace(false);
    try {
      localStorage.setItem(RUBRIC_TEMPLATE_CHOICE_KEY, id);
    } catch {
      /* 무시 */
    }
  };

  const selectedStandards = useMemo(
    () => allStandards.filter((s) => (draft.standard_ids ?? []).includes(s.id)),
    [allStandards, draft.standard_ids]
  );
  const standardsKey = selectedStandards.map((s) => s.id).join(',');
  const levelLabels = useMemo(() => getActiveLevelLabels(levelStepCount, levelDisplayLabels), [levelStepCount, levelDisplayLabels]);

  // 열릴 때 초기화 + 학습내용 검색(AI 없음)
  useEffect(() => {
    if (!open) {
      abortRef.current?.abort();
      return;
    }
    setStep(0);
    setError('');
    setNotice('');
    setConfirmReplace(false);
    setAppliedSteps({});
    setAppliedSig({});
    setFieldsResult(null);
    setRubricResult(null);
    setSheetResult(null);
    setSelectedKeys([]);
    setAppliedKeys(null);
    if (!selectedStandards.length) {
      setUnits([]);
      return;
    }
    let alive = true;
    setUnitsLoading(true);
    setUnitsError('');
    findLearningContentsForStandards({ workspaceId: workspaceId ?? '', standards: selectedStandards, categoryName, classScope })
      .then(({ units: list, source, gradeNumbers }) => {
        if (!alive) return;
        setSearchInfo({ source, gradeNumbers });
        setUnits(list);
        // 기본은 선택 없음(0개). 이 세션에서 적용한 선택이 있으면 목록에 남은 것만 복원
        const keysInList = new Set(list.flatMap((u) => u.lessons.map((l) => l.key)));
        const applied = (appliedKeysRef.current ?? []).filter((k) => keysInList.has(k));
        setSelectedKeys(applied);
      })
      .catch((e) => alive && setUnitsError(e instanceof Error ? e.message : '학습내용을 불러오지 못했습니다.'))
      .finally(() => alive && setUnitsLoading(false));
    void fetchAiEvalStatus().then((s) => alive && s.rate && setRate(s.rate));
    return () => {
      alive = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, standardsKey, categoryName, workspaceId, classScope?.gradeId, (classScope?.gradeNumbers ?? []).join(',')]);

  // 덮어쓰기 기본값: 비어 있는 칸만 체크
  useEffect(() => {
    if (!fieldsResult) return;
    setFieldOverwrite({
      unit_text: !(draft.unit_text ?? '').trim(),
      evaluation_element_text: !(draft.evaluation_element_text ?? '').trim(),
      evaluation_method_text: !(draft.evaluation_method_text ?? '').trim(),
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fieldsResult]);

  const sameKeys = (a: string[] | null, b: string[]) => !!a && a.length === b.length && a.every((k) => b.includes(k));
  const step1Applied = sameKeys(appliedKeys, selectedKeys) && selectedKeys.length > 0;
  /** 확정된 학습내용(목록 순서) */
  const chosen = useMemo(() => {
    const keys = appliedKeys ?? [];
    const out: Array<{ lesson: LearningContentMatchLesson; unit: LearningContentMatchUnit }> = [];
    for (const u of units ?? []) for (const l of u.lessons) if (keys.includes(l.key)) out.push({ lesson: l, unit: u });
    return out;
  }, [units, appliedKeys]);
  const lesson: LearningContentMatchLesson | null = chosen[0]?.lesson ?? null;
  const chosenUnits = useMemo(() => {
    const seen: string[] = [];
    chosen.forEach(({ lesson: l }) => {
      const u = (l.unitText || '').trim();
      if (u && !seen.includes(u)) seen.push(u);
    });
    return seen;
  }, [chosen]);
  const toggleLesson = (key: string) =>
    setSelectedKeys((prev) => (prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key]));
  const applyLessons = () => {
    if (!selectedKeys.length) return;
    setAppliedKeys([...selectedKeys]);
  };
  /** '선택 적용' 버튼: 적용 후 ✓ 적용됨을 잠깐 보여 주고 ② 단계로 */
  const advanceTimerRef = useRef<number | null>(null);
  useEffect(() => () => {
    if (advanceTimerRef.current) window.clearTimeout(advanceTimerRef.current);
  }, []);
  useEffect(() => {
    if (!open && advanceTimerRef.current) {
      window.clearTimeout(advanceTimerRef.current);
      advanceTimerRef.current = null;
    }
  }, [open]);
  const applyLessonsAndAdvance = () => {
    if (!selectedKeys.length) return;
    applyLessons();
    if (advanceTimerRef.current) window.clearTimeout(advanceTimerRef.current);
    advanceTimerRef.current = window.setTimeout(() => {
      advanceTimerRef.current = null;
      setStep((cur) => (cur === 0 ? 1 : cur));
      setConfirmReplace(false);
      setError('');
      setNotice('');
    }, 300);
  };

  const existingRubricForContext = useMemo(() => {
    if (rubricResult) return { task: rubricResult.task, items: rubricResult.items };
    const doc = parseEvaluationPlanWebDocumentData(existingCriterionAttachment?.form_data?.web_document);
    if (!doc) return null;
    const cols = doc.columns.filter((c) => c.isCriterion || c.isUserAdded);
    return {
      task: doc.exampleAnswer?.example ?? '',
      items: cols.map((c) => ({ header: c.header, cells: doc.rows.map((r) => r.cells[c.id] ?? '') })),
    };
  }, [rubricResult, existingCriterionAttachment]);

  const applyStandards = useMemo(
    () =>
      allStandards.map((s) => ({
        id: s.id,
        area_text: s.area_text ?? '',
        achievement_code: s.achievement_code ?? '',
        achievement_text: s.achievement_text ?? '',
      })),
    [allStandards]
  );

  const templateFieldTitles = useMemo(
    () =>
      selectedRubricTemplate
        ? collectRubricTemplateFields({
            plan: draft,
            standards: applyStandards,
            levelStepCount,
            levelDisplayLabels,
            structure: selectedRubricTemplate.structure,
          })
        : [],
    [selectedRubricTemplate, draft, applyStandards, levelStepCount, levelDisplayLabels]
  );

  const buildContext = useCallback(
    (forStep: 'fields' | 'rubric' | 'sheet'): AiEvalContext => {
      const keys = getActiveLevelKeys(levelStepCount);
      return {
        subject: categoryName,
        grade: lesson ? `${lesson.gradeNumber}학년` : '',
        // 여러 단원이면 순서대로 ', ' 로 이음(서버 규칙과 같음)
        unit: chosenUnits.length ? chosenUnits.join(', ') : draft.unit_text ?? '',
        learning: chosen.map(({ lesson: l }) => l.learningText).filter(Boolean).join(' / '),
        lessons: chosen.map(({ lesson: l }) => ({ unit: l.unitText ?? '', learning: l.learningText ?? '', codes: l.matchedCodes })),
        learningSiblings:
          forStep === 'fields' && chosen.length === 1
            ? chosen[0].unit.lessons.map((l) => l.learningText).filter((t) => t !== chosen[0].lesson.learningText).slice(0, 20)
            : [],
        standards: selectedStandards.map((s) => ({
          code: normalizeAchievementCode(s.achievement_code || '') || (s.achievement_code ?? ''),
          area: s.area_text ?? '',
          text: s.achievement_text ?? '',
          levels: keys.map((k) => String((s as unknown as Record<string, string>)[k] ?? '')),
        })),
        levelLabels,
        options,
        ...(forStep !== 'fields'
          ? {
              fields: {
                unit_text: draft.unit_text ?? '',
                evaluation_element_text: draft.evaluation_element_text ?? '',
                evaluation_method_text: draft.evaluation_method_text ?? '',
              },
            }
          : {}),
        ...(forStep === 'sheet' ? { rubric: existingRubricForContext } : {}),
        ...(forStep === 'rubric' && selectedRubricTemplate
          ? {
              templateFields: collectRubricTemplateFields({
                plan: draft,
                standards: applyStandards,
                levelStepCount,
                levelDisplayLabels,
                structure: selectedRubricTemplate.structure,
              }),
            }
          : {}),
      };
    },
    [categoryName, lesson, chosen, chosenUnits, draft, selectedStandards, levelLabels, options, levelStepCount, existingRubricForContext, selectedRubricTemplate, applyStandards, levelDisplayLabels]
  );

  const run = async (which: 'fields' | 'rubric' | 'sheet', force = false) => {
    setBusy(true);
    setError('');
    setNotice('');
    setConfirmReplace(false);
    abortRef.current?.abort();
    const ac = new AbortController();
    abortRef.current = ac;
    try {
      const out = await requestAiEval(which, buildContext(which), { signal: ac.signal, force });
      if (out.rate) setRate(out.rate);
      if (out.cached) setNotice('같은 조건의 이전 결과를 불러왔어요. 새로 만들려면 [다시 만들기].');
      if (which === 'fields') {
        // 평가요소는 1개(예전/캐시 결과가 여러 줄이면 첫 요소만)
        const r = out.result as AiEvalFieldsResult;
        setFieldsResult({ ...r, evaluation_element_text: toSingleEvaluationElement(r.evaluation_element_text) });
      }
      if (which === 'rubric') setRubricResult(out.result as AiEvalRubricResult);
      if (which === 'sheet') setSheetResult(out.result as AiEvalSheetResult);
    } catch (e) {
      if (e instanceof DOMException && e.name === 'AbortError') return;
      setError(e instanceof Error ? e.message : 'AI 요청에 실패했습니다.');
    } finally {
      setBusy(false);
    }
  };

  const markApplied = (i: number) => setAppliedSteps((prev) => ({ ...prev, [i]: true }));

  const applyFields = () => {
    if (!fieldsResult) return;
    const patch: Partial<EvaluationPlanDraft> = {};
    FIELD_KEYS.forEach((k) => {
      if (fieldOverwrite[k] && fieldsResult[k].trim()) (patch as Record<string, string>)[k] = fieldsResult[k];
    });
    onApplyFields(patch);
    setAppliedSig((p) => ({ ...p, fields: { result: fieldsSig, patch: patch as Partial<Record<FieldKey, string>> } }));
    markApplied(1);
    setStep(2);
  };

  const applyRubric = () => {
    if (!rubricResult) return;
    if (hasCriterion && !confirmReplace) {
      setConfirmReplace(true);
      return;
    }
    const doc = selectedRubricTemplate
      ? buildRubricWebDocumentFromTemplate({
          plan: draft,
          standards: applyStandards,
          levelStepCount,
          levelDisplayLabels,
          structure: selectedRubricTemplate.structure,
          rubric: rubricResult,
        })
      : buildRubricWebDocumentFromAi({
          plan: draft,
          standards: applyStandards,
          levelStepCount,
          levelDisplayLabels,
          rubric: rubricResult,
        });
    // 제목: 기본/자동 제목이면 과목 제목, 사용자 양식의 직접 정한 제목은 유지
    doc.title = resolveDocTitle('criterion', doc.title, subjectNamesFromStandards(selectedStandards));
    onApplyAttachment('criterion', {
      kind: 'web_document',
      slot: 'criterion',
      registered_at: new Date().toISOString(),
      preview_label: '평가 기준안',
      form_data: { web_document: doc },
    });
    setAppliedSig((p) => ({ ...p, rubric: { result: rubricSig, target: sigOf(doc) } }));
    setConfirmReplace(false);
    markApplied(2);
    setStep(3);
  };

  const sigOf = (v: unknown) => {
    try {
      return JSON.stringify(v ?? null);
    } catch {
      return String(Math.random());
    }
  };
  const fieldsSig = useMemo(() => (fieldsResult ? sigOf(fieldsResult) : ''), [fieldsResult]);
  const rubricSig = useMemo(() => (rubricResult ? sigOf({ r: rubricResult, t: rubricTemplateId }) : ''), [rubricResult, rubricTemplateId]);
  const sheetSig = useMemo(() => (sheetResult ? sigOf(sheetResult) : ''), [sheetResult]);
  const criterionTargetSig = useMemo(
    () => sigOf(existingCriterionAttachment?.form_data?.web_document ?? null),
    [existingCriterionAttachment]
  );
  const sheetTargetSig = useMemo(() => sigOf(existingSheetAttachment?.form_data?.sheet_canvas ?? null), [existingSheetAttachment]);

  const fieldsApplied = (() => {
    const a = appliedSig.fields;
    if (!a || !fieldsResult || a.result !== fieldsSig) return false;
    // 지금 체크 상태로 적용할 내용이 적용 당시와 같고, 폼 값도 그대로여야 함
    const keys = FIELD_KEYS.filter((k) => fieldOverwrite[k] && fieldsResult[k].trim());
    const appliedKeys = Object.keys(a.patch) as FieldKey[];
    if (keys.length !== appliedKeys.length || keys.some((k) => !appliedKeys.includes(k))) return false;
    return appliedKeys.every((k) => (draft[k] ?? '') === a.patch[k]);
  })();
  const rubricApplied = Boolean(
    appliedSig.rubric && rubricResult && appliedSig.rubric.result === rubricSig && appliedSig.rubric.target === criterionTargetSig
  );
  const sheetApplied = Boolean(
    appliedSig.sheet && sheetResult && appliedSig.sheet.result === sheetSig && appliedSig.sheet.target === sheetTargetSig
  );

  const sheetBuild = useMemo(() => {
    if (!sheetResult) return null;
    const built = buildSheetCanvasDocumentFromAi(sheetResult);
    // 평가지 머리말 제목: 과목 이름(예: '국어 평가지')
    built.document = { ...built.document, title: subjectDocTitle('sheet', subjectNamesFromStandards(selectedStandards)) };
    return built;
  }, [sheetResult, selectedStandards]);

  const applySheet = () => {
    if (!sheetBuild) return;
    if (hasSheet && !confirmReplace) {
      setConfirmReplace(true);
      return;
    }
    onApplyAttachment('sheet', {
      kind: 'web_document',
      slot: 'sheet',
      registered_at: new Date().toISOString(),
      preview_label: sheetBuild.document.title || '평가지',
      form_data: { sheet_canvas: sheetBuild.document },
    });
    setAppliedSig((p) => ({ ...p, sheet: { result: sheetSig, target: sigOf(sheetBuild.document) } }));
    setConfirmReplace(false);
    markApplied(3);
    setNotice('평가지를 넣었어요. [평가지 등록됨]에서 열어 다듬은 뒤 평가계획을 [저장]하세요.');
  };

  if (!open) return null;

  const optionsBar = (
    <div className="cp-ai-eval-options" role="group" aria-label="생성 옵션">
      <label>
        <span>평가 유형</span>
        <select value={options.assessmentType} onChange={(e) => setOptions((o) => ({ ...o, assessmentType: e.target.value as AiEvalOptions['assessmentType'] }))}>
          {AI_EVAL_ASSESSMENT_TYPES.map((t) => (
            <option key={t} value={t}>{t}</option>
          ))}
        </select>
      </label>
      <label>
        <span>난이도</span>
        <select value={options.difficulty} onChange={(e) => setOptions((o) => ({ ...o, difficulty: e.target.value as AiEvalOptions['difficulty'] }))}>
          {AI_EVAL_DIFFICULTIES.map((t) => (
            <option key={t} value={t}>{t}</option>
          ))}
        </select>
      </label>
      <label>
        <span>문항 수</span>
        <select value={options.questionCount} onChange={(e) => setOptions((o) => ({ ...o, questionCount: Number(e.target.value) }))}>
          {[1, 2, 3, 4, 5].map((n) => (
            <option key={n} value={n}>{n}</option>
          ))}
        </select>
      </label>
      <span className="cp-ai-eval-options-level">성취수준 {levelLabels.length}단계 ({levelLabels.join('·')})</span>
    </div>
  );

  const stepActions = (
    which: 'fields' | 'rubric' | 'sheet',
    hasResult: boolean,
    onApply: () => void,
    applyLabel = '적용',
    isApplied = false
  ) => (
    <div className={`cp-ai-eval-step-actions${hasResult ? ' cp-ai-eval-step-actions--split' : ''}`}>
      {!hasResult ? (
        <button type="button" className="cp-ai-feedback-btn cp-ai-eval-generate" disabled={busy || !selectedStandards.length} onClick={() => void run(which)}>
          <AiSparkIcon />
          {busy ? 'AI 작성 중…' : 'AI로 만들기'}
        </button>
      ) : (
        <>
          <button type="button" className="cp-btn-secondary" disabled={busy} onClick={() => void run(which, true)}>
            {busy ? 'AI 작성 중…' : '다시 만들기'}
          </button>
          {isApplied ? (
            <button type="button" className="cp-btn-primary cp-ai-eval-applied" disabled aria-disabled="true">
              ✓ 적용됨
            </button>
          ) : (
            <button type="button" className={confirmReplace ? 'cp-btn-primary cp-ai-eval-danger' : 'cp-btn-primary'} disabled={busy} onClick={onApply}>
              {confirmReplace ? '기존 내용 교체' : applyLabel}
            </button>
          )}
        </>
      )}
    </div>
  );

  return (
    <div
      className="cp-modal-backdrop cp-ai-eval-backdrop"
      role="presentation"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget && !busy) onClose();
      }}
      onKeyDown={(e) => {
        if (e.key === 'Escape' && !busy) {
          e.stopPropagation();
          onClose();
        }
      }}
    >
      <div className="cp-modal cp-ai-eval-modal" role="dialog" aria-modal="true" aria-labelledby="cp-ai-eval-title" onMouseDown={(e) => e.stopPropagation()} onClick={(e) => e.stopPropagation()}>
        <div className="cp-modal-document-header cp-ai-eval-header">
          <h3 id="cp-ai-eval-title">AI 평가 구성</h3>
          <button
            type="button"
            className="cp-ai-eval-undo-icon"
            disabled={!canUndo || busy}
            onClick={() => {
              onUndo();
              setAppliedSteps({});
              setNotice('AI 적용 전 상태로 되돌렸어요.');
            }}
            title="되돌리기"
            aria-label="되돌리기"
          >
            <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <path d="M9 14 4 9l5-5" />
              <path d="M4 9h10.5a5.5 5.5 0 0 1 0 11H11" />
            </svg>
          </button>
          {rate ? <span className="cp-ai-eval-rate" title={`${rate.windowMinutes}분당 최대 ${rate.max}회`}>남은 요청 {rate.remaining}/{rate.max}</span> : null}
          <button type="button" className="cp-ai-eval-undo-icon cp-ai-eval-close-icon" disabled={busy} onClick={onClose} title="닫기" aria-label="닫기">
            <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" aria-hidden="true">
              <path d="M6 6l12 12M18 6 6 18" />
            </svg>
          </button>
        </div>
        <ol className="cp-ai-eval-steps" aria-label="단계">
          {STEP_TITLES.map((t, i) => (
            <li key={t} className={`${i === step ? 'is-active' : ''}${(i === 0 ? step1Applied : appliedSteps[i]) ? ' is-done' : ''}`}>
              <button
                type="button"
                disabled={busy}
                aria-current={i === step ? 'step' : undefined}
                onClick={() => {
                  if (i === step) return;
                  // ① 에서 다른 단계로 갈 때 고른 학습내용이 아직 적용 전이면 자동 적용(예전 '다음'과 같음)
                  if (step === 0 && !step1Applied && selectedKeys.length) applyLessons();
                  setConfirmReplace(false);
                  setError('');
                  setNotice('');
                  setStep(i);
                }}
              >
                <span className="cp-ai-eval-step-no" aria-hidden>{(i === 0 ? step1Applied : appliedSteps[i]) ? '✓' : i + 1}</span>
                {t}
              </button>
            </li>
          ))}
        </ol>
        <div className="cp-modal-body cp-ai-eval-body">
          {!selectedStandards.length ? (
            <p className="cp-ai-eval-empty">선택한 성취기준이 없어요. 평가계획 등록 화면에서 성취기준을 먼저 선택해 주세요.</p>
          ) : null}

          {step === 0 && selectedStandards.length ? (
            <section>
              <p className="cp-ai-eval-hint">
                {classScope
                  ? `현재 학급(${(classScope.gradeNumbers ?? []).map((g) => `${g}학년`).join('·') || '학년 미지정'})의 ${
                      searchInfo?.source === 'workspace' ? '내 학습내용' : '관리자 학습내용'
                    } 중 선택한 성취기준이 들어 있는 차시예요.${
                      searchInfo?.source === 'admin' ? ' (이 학년의 내 학습내용이 없어 관리자 학습내용을 보여 드려요)' : ''
                    } 평가할 차시를 고르세요.`
                  : '선택한 성취기준이 들어 있는 관리자 학습내용이에요. 평가할 차시를 고르세요.'}
              </p>
              <div className="cp-ai-eval-standards" aria-label="선택한 성취기준">
                {selectedStandards.map((st) => {
                  const code = normalizeAchievementCode(st.achievement_code || '') || (st.achievement_code ?? '').replace(/^\[|\]$/g, '');
                  return (
                    <div key={st.id} className="cp-ai-eval-standard">
                      {code ? <span className="cp-ai-eval-standard-code">[{code.replace(/^\[|\]$/g, '')}]</span> : null}
                      <span className="cp-ai-eval-standard-text">{st.achievement_text || '(성취기준 내용 없음)'}</span>
                    </div>
                  );
                })}
              </div>
              {unitsLoading ? <p className="cp-ai-eval-hint" role="status">학습내용을 찾는 중…</p> : null}
              {unitsError ? <p className="cp-ai-eval-error" role="alert">{unitsError}</p> : null}
              {units && !unitsLoading && units.length === 0 ? (
                <p className="cp-ai-eval-empty">일치하는 학습내용이 없어요. 학습내용 없이 성취기준만으로 진행할 수 있어요.</p>
              ) : null}
              <div className="cp-ai-eval-units">
                {(units ?? []).map((u, ui, arr) => {
                  const groupLabel = `${u.gradeNumber}학년 ${learningSubCategoryLabel(u.subCategory)} · ${u.templateName}`;
                  const prev = ui > 0 ? arr[ui - 1] : null;
                  const showHeading =
                    !prev ||
                    `${prev.gradeNumber}학년 ${learningSubCategoryLabel(prev.subCategory)} · ${prev.templateName}` !== groupLabel ||
                    prev.source !== u.source;
                  return (
                  <React.Fragment key={u.key}>
                  {showHeading ? (
                    <div className="cp-ai-eval-group-heading">
                      <span>{groupLabel}</span>
                      <span className="cp-ai-eval-group-source">{u.source === 'workspace' ? '내 학습내용' : '관리자 학습내용'}</span>
                    </div>
                  ) : null}
                  <div className="cp-ai-eval-unit">
                    <div className="cp-ai-eval-unit-head">
                      <strong>{u.unitText}</strong>
                      <span className="cp-ai-eval-badge">{u.source === 'workspace' ? '내 학습내용' : '관리자 학습내용'} · {u.gradeNumber}학년 {learningSubCategoryLabel(u.subCategory)} · {u.templateName}</span>
                    </div>
                    <ul>
                      {u.lessons.map((l) => (
                        <li key={l.key}>
                          <label className={`cp-ai-eval-lesson${selectedKeys.includes(l.key) ? ' is-selected' : ''}`}>
                            <input type="checkbox" checked={selectedKeys.includes(l.key)} onChange={() => toggleLesson(l.key)} />
                            <span className="cp-ai-eval-lesson-text">{l.learningText || '(학습내용 없음)'}</span>
                            <span className="cp-ai-eval-lesson-codes">{l.matchedCodes.map((c) => `[${c}]`).join(' ')}</span>
                          </label>
                        </li>
                      ))}
                    </ul>
                  </div>
                  </React.Fragment>
                  );
                })}
              </div>
            </section>
          ) : null}

          {step === 1 && selectedStandards.length ? (
            <section>
              {!fieldsResult ? (
                  <p className="cp-ai-eval-hint">
                  {chosen.length ? `「${chosenUnits.join(', ')}」의 학습내용 ${chosen.length}개를` : '성취기준을'} 바탕으로 단원명·평가요소·수업·평가 방법을 만들어요.
                </p>
              ) : null}
              {optionsBar}
              {fieldsResult ? (
                <div className="cp-ai-eval-preview">
                  {FIELD_KEYS.map((k) => {
                    const cur = (draft[k] ?? '').trim();
                    return (
                      <div key={k} className="cp-ai-eval-field">
                        <div className="cp-ai-eval-field-head">
                          <strong>{FIELD_LABELS[k]}</strong>
                          <label className="cp-ai-eval-overwrite">
                            <input type="checkbox" checked={fieldOverwrite[k]} onChange={(e) => setFieldOverwrite((p) => ({ ...p, [k]: e.target.checked }))} />
                            {cur ? '기존 값 덮어쓰기' : '적용'}
                          </label>
                        </div>
                        <p className="cp-ai-eval-field-new">{fieldsResult[k] || '—'}</p>
                        {cur ? <p className="cp-ai-eval-field-old">현재: {cur}</p> : null}
                      </div>
                    );
                  })}
                </div>
              ) : null}
            </section>
          ) : null}

          {step === 2 && selectedStandards.length ? (
            <section>
              {!chosen.length ? <p className="cp-ai-eval-hint">학습내용을 고르지 않아 성취기준만으로 만들어요. (① 단계에서 학습내용을 선택하면 더 알맞게 만들어요)</p> : null}
              {!rubricResult ? <p className="cp-ai-eval-hint">평가계획 항목과 성취수준을 바탕으로 기준안(평가 과제·평가 항목·채점 기준·유의사항)을 만들어요.</p> : null}
              {optionsBar}
              <div className="cp-ai-eval-template-pick" role="radiogroup" aria-label="기준안 양식">
                <span className="cp-ai-eval-template-pick-label">기준안 양식</span>
                {[{ id: DEFAULT_RUBRIC_TEMPLATE, name: '기본 양식' }, ...rubricTemplates].map((t) => (
                  <button
                    key={t.id}
                    type="button"
                    role="radio"
                    aria-checked={rubricTemplateId === t.id}
                    className={`cp-ai-eval-template-pill${rubricTemplateId === t.id ? ' is-active' : ''}`}
                    disabled={busy}
                    onClick={() => chooseRubricTemplate(t.id)}
                  >
                    {t.name}
                  </button>
                ))}
                {selectedRubricTemplate ? (
                  <small className="cp-ai-eval-template-pick-hint">양식의 칸 제목에 맞춰 내용을 채워요. 표·라벨은 그대로 둬요.</small>
                ) : null}
              </div>
              {rubricResult ? (
                <div className="cp-ai-eval-preview">
                  {selectedRubricTemplate && (rubricResult.template_values ?? []).some((v) => v.value.trim()) ? (
                    <div className="cp-ai-eval-template-fill">
                      <h4>「{selectedRubricTemplate.name}」 양식 칸</h4>
                      {templateFieldTitles.map((f) => {
                        const v = (rubricResult.template_values ?? []).find((x) => x.id === f.id)?.value?.trim() ?? '';
                        return (
                          <p key={f.id} className="cp-ai-eval-pre">
                            <strong>{f.title}</strong> {v || <em className="cp-ai-eval-template-empty">(비워 둠)</em>}
                          </p>
                        );
                      })}
                    </div>
                  ) : null}
                  {(rubricResult.method_tags ?? []).length ? (
                    <div className="cp-ai-eval-tags">
                      <span className="cp-ai-eval-label">평가 방법</span>
                      {(rubricResult.method_tags ?? []).map((t) => (
                        <span key={t} className="cp-ai-eval-chip">{t}</span>
                      ))}
                    </div>
                  ) : null}
                  <h4>{rubricResult.task_title || '평가 과제'}</h4>
                  {(rubricResult.task_activities ?? []).length ? (
                    <p className="cp-ai-eval-pre">{(rubricResult.task_activities ?? []).map((a) => `▪ ${a}`).join('\n')}</p>
                  ) : null}
                  <p className="cp-ai-eval-pre">{rubricResult.task}</p>
                  <div className="cp-ai-eval-table-wrap">
                    <table className="cp-ai-eval-table">
                      <thead>
                        <tr>
                          <th>채점 기준</th>
                          {rubricResult.items.map((it, i) => (
                            <th key={i}>{it.header}</th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {levelLabels.map((lab, li) => (
                          <tr key={lab}>
                            <th>
                              {lab}
                              {rubricResult.level_memos[li] ? <small>{rubricResult.level_memos[li]}</small> : null}
                            </th>
                            {rubricResult.items.map((it, i) => (
                              <td key={i}>{it.cells[li]}</td>
                            ))}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  {rubricResult.example_answer_top ? (
                    <p className="cp-ai-eval-pre"><strong>예시 답안(최상위)</strong> {rubricResult.example_answer_top}</p>
                  ) : null}
                  {rubricResult.example_answer_mid ? (
                    <p className="cp-ai-eval-pre"><strong>예시 답안(보통)</strong> {rubricResult.example_answer_mid}</p>
                  ) : null}
                  {rubricResult.notice ? (
                    <p className="cp-ai-eval-pre"><strong>평가 시 유의사항</strong> {rubricResult.notice}</p>
                  ) : null}
                </div>
              ) : null}
              {confirmReplace ? <p className="cp-ai-eval-warn">이미 등록된 기준안이 있어요. 교체할까요? (저장 전까지는 [되돌리기]로 복구할 수 있어요)</p> : null}
            </section>
          ) : null}

          {step === 3 && selectedStandards.length ? (
            <section>
              {!chosen.length ? <p className="cp-ai-eval-hint">학습내용을 고르지 않아 성취기준만으로 만들어요. (① 단계에서 학습내용을 선택하면 더 알맞게 만들어요)</p> : null}
              {!(sheetResult && sheetBuild) ? <p className="cp-ai-eval-hint">기준안의 평가 과제를 학생용 A4 평가지(텍스트·표·답란, 필요 시 소스코드 블럭)로 만들어요. 채점 기준은 평가지에 넣지 않아요.</p> : null}
              {optionsBar}
              {sheetResult && sheetBuild ? (
                <div className="cp-ai-eval-preview cp-ai-eval-sheet-preview">
                  <p className="cp-ai-eval-pageinfo">
                    A4 <strong>{sheetBuild.pageCount}쪽</strong>으로 배치돼요 · 문항 경계에서 쪽을 나눠요
                    {sheetBuild.overflow ? <span className="cp-ai-eval-pageinfo-warn"> · 한 쪽보다 긴 블럭이 있어 편집기에서 확인이 필요해요</span> : null}
                  </p>
                  {sheetResult.title ? <h4 className="cp-ai-eval-sheet-title">{sheetResult.title}</h4> : null}
                  {sheetResult.sections.map((s, i) => (
                    <div key={i} className={`cp-ai-eval-sheet-sec is-${s.type}`}>
                      {s.type === 'heading' ? <strong>{s.text}</strong> : null}
                      {s.type === 'text' ? <p className="cp-ai-eval-pre">{s.text}</p> : null}
                      {s.type === 'table' && s.text ? <strong>{s.text}</strong> : null}
                      {s.type === 'table' ? (
                        <table className="cp-ai-eval-table">
                          <tbody>
                            {s.rows.map((r, ri) => (
                              <tr key={ri}>
                                {r.map((c, ci) => (ri === 0 ? <th key={ci}>{c}</th> : <td key={ci}>{c}</td>))}
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      ) : null}
                      {s.type === 'answerBox' ? (
                        <>
                          {s.text ? <p className="cp-ai-eval-pre">{s.text}</p> : null}
                          <div className="cp-ai-eval-answer-box" style={{ height: `${s.lines * 18}px` }} />
                        </>
                      ) : null}
                      {s.type === 'html' ? (
                        // 정리된(스크립트 없는) HTML 정적 미리보기
                        <div className="cp-ai-eval-html" dangerouslySetInnerHTML={{ __html: sanitizeSheetCodeHtml(s.html) }} />
                      ) : null}
                    </div>
                  ))}
                </div>
              ) : null}
              {confirmReplace ? <p className="cp-ai-eval-warn">이미 등록된 평가지가 있어요. 교체할까요? (저장 전까지는 [되돌리기]로 복구할 수 있어요)</p> : null}
            </section>
          ) : null}

          {error ? <p className="cp-ai-eval-error" role="alert">{error}</p> : null}
          {notice ? <p className="cp-ai-eval-hint" role="status">{notice}</p> : null}
        </div>
        <div className="cp-ai-eval-footer-bar">
          {step === 0 && units && units.length ? (
            <div className="cp-ai-eval-step-actions cp-ai-eval-step-actions--full">
              {step1Applied ? (
                <button type="button" className="cp-btn-primary cp-ai-eval-applied" disabled aria-disabled="true">
                  ✓ <span className="cp-ai-eval-btn-count">{selectedKeys.length}개 선택</span>
                  <span className="cp-ai-eval-btn-sep" aria-hidden="true">·</span>
                  적용됨
                </button>
              ) : (
                <button type="button" className="cp-btn-primary" disabled={busy || !selectedKeys.length} onClick={applyLessonsAndAdvance}>
                  <span className="cp-ai-eval-btn-count">{selectedKeys.length}개 선택</span>
                  <span className="cp-ai-eval-btn-sep" aria-hidden="true">·</span>
                  선택 적용
                </button>
              )}
            </div>
          ) : null}
          {step === 1 && selectedStandards.length ? stepActions('fields', Boolean(fieldsResult), applyFields, '평가계획에 적용', fieldsApplied) : null}
          {step === 2 && selectedStandards.length ? stepActions('rubric', Boolean(rubricResult), applyRubric, '기준안에 적용', rubricApplied) : null}
          {step === 3 && selectedStandards.length ? stepActions('sheet', Boolean(sheetResult), applySheet, '평가지에 적용', sheetApplied) : null}
        </div>
      </div>
    </div>
  );
};

export default AiEvaluationComposeModal;
